import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip, 
  TextField,
  MenuItem,
  Grid,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { formatDate } from '../../utils/formatters';

interface MarginPosition {
  id: number;
  margin: string;
  fromAssetCode: string;
  toAssetCode: string;
  credit: string;
  userId: number;
  username?: string;
  orderType: string;
  quantity: string;
  leverage: string;
  entryPrice: string;
  status: string;
  createdAt: string;
}

interface MarginApyReturn {
  id: number;
  userId: number;
  username?: string;
  marginOrderId: number;
  apyAmountAdded: string;
  dailyApyPercentage: string;
  annualApyPercentage: string;
  createdAt: string;
  position?: MarginPosition;
}

interface MarginApyFilters {
  status: string;
  userId: string;
  orderType: string;
  page: number;
  limit: number;
}

interface MarginApyReturnsResponse {
  data: MarginApyReturn[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const fetchMarginApyReturns = async (filters: MarginApyFilters): Promise<MarginApyReturnsResponse> => {
  try {
    const params: any = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.userId) params.userId = parseInt(filters.userId);
    if (filters.orderType) params.orderType = filters.orderType;
    
    params.limit = filters.limit;
    params.page = filters.page;

    const response = await api.get('/api/v1/admin-panel/margin/apy', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching margin APY returns:', error);
    throw error;
  }
};

const MarginApyPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apyReturns, setApyReturns] = useState<MarginApyReturn[]>([]);
  const [totalReturns, setTotalReturns] = useState(0);
  
  const [filters, setFilters] = useState<MarginApyFilters>({
    status: '',
    userId: '',
    orderType: '',
    page: 1,
    limit: 50
  });

  const loadApyReturns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchMarginApyReturns(filters);
      
      if (response?.data && Array.isArray(response.data)) {
        setApyReturns(response.data);
        setTotalReturns(response.total || 0);
      } else {
        console.error('Invalid response structure:', response);
        setApyReturns([]);
        setTotalReturns(0);
        setError('Не удалось загрузить APY доходы: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading margin APY returns:', err);
      setApyReturns([]);
      setTotalReturns(0);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке APY доходов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApyReturns();
  }, [filters]);

  const handleFilterChange = (field: keyof MarginApyFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleRefresh = () => {
    loadApyReturns();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      userId: '',
      orderType: '',
      page: 1,
      limit: 50
    });
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FilterListIcon sx={{ mr: 1 }} />
        Фильтры
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="ID пользователя"
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            size="small"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label="Тип позиции"
            select
            value={filters.orderType}
            onChange={(e) => handleFilterChange('orderType', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все типы</MenuItem>
            <MenuItem value="long">LONG</MenuItem>
            <MenuItem value="short">SHORT</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={clearFilters} size="small">
              Очистить
            </Button>
            <Button variant="contained" onClick={handleRefresh} size="small" startIcon={<RefreshIcon />}>
              Обновить
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderApyTable = () => {
    if (loading) {
      return (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error}
        </Alert>
      );
    }

    if (!apyReturns || apyReturns.length === 0) {
      return (
        <Alert severity="info">
          APY доходы не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID записи</TableCell>
            <TableCell>Пользователь</TableCell>
            <TableCell>ID позиции</TableCell>
            <TableCell>Сумма APY</TableCell>
            <TableCell>Дневной %</TableCell>
            <TableCell>Годовой %</TableCell>
            <TableCell>Дата создания</TableCell>
            <TableCell>Информация о позиции</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apyReturns.map((apyReturn) => (
            <TableRow key={apyReturn.id}>
              <TableCell>{apyReturn.id}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">ID: {apyReturn.userId}</Typography>
                  {apyReturn.username && (
                    <Typography variant="caption" color="text.secondary">
                      {apyReturn.username}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>{apyReturn.marginOrderId}</TableCell>
              <TableCell>
                <Typography variant="body2" color="success.main">
                  +{parseFloat(apyReturn.apyAmountAdded).toFixed(8)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip 
                  label={`${parseFloat(apyReturn.dailyApyPercentage).toFixed(4)}%`}
                  color="primary"
                  size="small" 
                />
              </TableCell>
              <TableCell>
                <Chip 
                  label={`${parseFloat(apyReturn.annualApyPercentage).toFixed(2)}%`}
                  color="success"
                  size="small" 
                />
              </TableCell>
              <TableCell>{formatDate(apyReturn.createdAt)}</TableCell>
              <TableCell>
                {apyReturn.position ? (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">
                        {apyReturn.position.fromAssetCode}/{apyReturn.position.toAssetCode}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="caption" display="block">
                          <strong>Тип:</strong> {apyReturn.position.orderType === 'long' ? 'LONG' : 'SHORT'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Количество:</strong> {parseFloat(apyReturn.position.quantity).toFixed(8)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Плечо:</strong> {apyReturn.position.leverage}x
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Цена входа:</strong> {parseFloat(apyReturn.position.entryPrice).toFixed(8)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Статус позиции:</strong> {apyReturn.position.status}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Создана:</strong> {formatDate(apyReturn.position.createdAt)}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Информация недоступна
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Calculate summary statistics
  const totalApyAmount = apyReturns.reduce((sum, item) => sum + parseFloat(item.apyAmountAdded), 0);
  const averageDailyApy = apyReturns.length > 0 
    ? apyReturns.reduce((sum, item) => sum + parseFloat(item.dailyApyPercentage), 0) / apyReturns.length 
    : 0;
  const averageAnnualApy = apyReturns.length > 0 
    ? apyReturns.reduce((sum, item) => sum + parseFloat(item.annualApyPercentage), 0) / apyReturns.length 
    : 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        APY доходы
      </Typography>
      
      {renderFilters()}
      
      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {totalApyAmount.toFixed(8)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Общая сумма APY
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary.main">
              {averageDailyApy.toFixed(4)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Средний дневной APY
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary.main">
              {averageAnnualApy.toFixed(2)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Средний годовой APY
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6">
              {totalReturns}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Всего записей
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Найдено записей: {totalReturns}
        </Typography>
        {renderApyTable()}
      </Paper>
    </Box>
  );
};

export default MarginApyPage;
